import { CodeTypes } from 'src/components/middle-school-view/single-session-code';

export enum XApiEventKeys {
  viewed = 'viewed',
  selectedPinnedCard = 'selected-pinned-card',
  selectedStatusFilter = 'selected-status-filter',
  selectedClassFilter = 'selected-class-filter',
  selectedSubjectFilter = 'selected-subject-filter',
  selectedFeedback = 'selected-feedback',
  selectedAppsCard = 'selected-apps-card',
  openedEducatorHome = 'opened-educator-home',
  openedAvatarBuilder = 'opened-avatar-builder',
  openedFeedback = 'opened-feedback',
  openedAssignment = 'opened-assignment',
  assignmentMarkedDone = 'assignment-marked-done',
  assignmentMarkedToDo = 'assignment-marked-to-do',
  assignmentStatusChangeCancelled = 'assignment-status-change-cancelled',
  assignmentStatusStillWorking = 'assignment-status-still-working',
  classCodeModalSuccess = 'class-code-modal-success',
  classCodeModalFailure = 'class-code-modal-failure',
  classCodeModalOpened = 'class-code-modal-opened',
}

type CommonXApiContextPayload = {
  result: {
    isRightClick: boolean;
    view: string;
    currentDisplayedAssignments?: number;
    currentTotalAssignments?: number;
    currentClassesDisplayed?: Array<string>;
    currentProductFilters?: Array<string>;
    currentStatusFilters?: Array<string>;
    currentTotalAssignmentsMarkedDone?: number;
    currentTotalAssignmentsWithFeedback?: number;
  };
};

type DestinationUrlResultExtension = {
  result: {
    destinationUrl: string;
  };
};

type AssignmentStatusResultExtension = {
  result: {
    assignmentStatus: string;
  };
};

type StatusFiltersSelectedResultExtension = {
  result: {
    statusFiltersSelected: string;
  };
};

type SubjectFiltersSelectedResultExtension = {
  result: {
    productFiltersSelected: string;
  };
};

type ClassFiltersSelectedResultExtension = {
  result: {
    classFiltersSelected: string;
  };
};

type ClassCodeEventResultExtension = {
  result: {
    isSuccess: boolean;
    shouldJoinClass?: boolean;
    source?: CodeTypes | null;
  };
};

export enum ASSIGNMENT_STUDENT_HOME_SECTION {
  todo = 'todo',
  pastWork = 'past-work',
}

export enum MS_ASSIGNMENT_STATUSES {
  STARTED = 'started',
  NOT_STARTED = 'not-started',
  HANDED_IN = 'handed-in',
}

export type AssignmentCommonXApiContextPayload = {
  activityId?: string; // Desmos-only.
  assignmentBusinessKey?: string;
  assignmentClassBk?: string | Array<string>;
  assignmentMarkedDone?: boolean | null;
  assignmentStatus?: ASSIGNMENT_STUDENT_HOME_SECTION | MS_ASSIGNMENT_STATUSES;
  cardstackBusinessKey?: string;
  classGrade: string;
  customContentId?: string;
  domain?: string;
  editionId?: string; // Desmos-only.
  googleSlideDeckId?: string;
  instanceId?: string; // Desmos-only.
  isCustom: boolean;
  lessonBusinessKey?: string;
  unitBusinessKey?: string;
  unitRevisionAlias?: string;
  unitRevisionNumber?: number;
};

export type AssignmentEventPayload = CommonXApiContextPayload['result'] &
  AssignmentCommonXApiContextPayload;

export type AssignmentEventContextPayload = {
  result: AssignmentEventPayload;
};

export type SelectedPinnedCardContextPayload = {
  result: CommonXApiContextPayload['result'] & {
    domain: string;
    pinnedCardTitle: string;
  };
};

export type ClassCodeEventContextPayload = {
  result: CommonXApiContextPayload['result'] & {
    classCode?: string;
  };
};

export type SelectedAppsCardEventContextPayload = {
  result: CommonXApiContextPayload['result'] & {
    productId: string;
    appCardName: string;
  };
};

export type XApiResultExtensions = {
  [XApiEventKeys.viewed]: never;
  [XApiEventKeys.openedAvatarBuilder]: DestinationUrlResultExtension;
  [XApiEventKeys.selectedStatusFilter]: StatusFiltersSelectedResultExtension;
  [XApiEventKeys.selectedSubjectFilter]: SubjectFiltersSelectedResultExtension;
  [XApiEventKeys.selectedClassFilter]: ClassFiltersSelectedResultExtension;
  [XApiEventKeys.selectedFeedback]: DestinationUrlResultExtension;
  [XApiEventKeys.openedAssignment]: DestinationUrlResultExtension;
  [XApiEventKeys.openedEducatorHome]: never;
  [XApiEventKeys.selectedPinnedCard]: DestinationUrlResultExtension;
  [XApiEventKeys.assignmentMarkedToDo]: DestinationUrlResultExtension &
    AssignmentStatusResultExtension;
  [XApiEventKeys.assignmentMarkedDone]: DestinationUrlResultExtension &
    AssignmentStatusResultExtension;
  [XApiEventKeys.assignmentStatusChangeCancelled]: DestinationUrlResultExtension &
    AssignmentStatusResultExtension;
  [XApiEventKeys.assignmentStatusStillWorking]: DestinationUrlResultExtension &
    AssignmentStatusResultExtension;
  [XApiEventKeys.classCodeModalFailure]: ClassCodeEventResultExtension;
  [XApiEventKeys.classCodeModalSuccess]: ClassCodeEventResultExtension;
};

export type XApiContextExtensions = {
  [XApiEventKeys.viewed]: CommonXApiContextPayload;
  [XApiEventKeys.openedAvatarBuilder]: CommonXApiContextPayload;
  [XApiEventKeys.openedAssignment]: AssignmentEventContextPayload;
  [XApiEventKeys.openedEducatorHome]: CommonXApiContextPayload;
  [XApiEventKeys.selectedStatusFilter]: CommonXApiContextPayload;
  [XApiEventKeys.selectedSubjectFilter]: CommonXApiContextPayload;
  [XApiEventKeys.selectedClassFilter]: CommonXApiContextPayload;
  [XApiEventKeys.selectedFeedback]: AssignmentEventContextPayload;
  [XApiEventKeys.selectedPinnedCard]: SelectedPinnedCardContextPayload;
  [XApiEventKeys.selectedAppsCard]: SelectedAppsCardEventContextPayload;
  [XApiEventKeys.assignmentMarkedToDo]: AssignmentEventContextPayload;
  [XApiEventKeys.assignmentMarkedDone]: AssignmentEventContextPayload;
  [XApiEventKeys.assignmentStatusChangeCancelled]: AssignmentEventContextPayload;
  [XApiEventKeys.assignmentStatusStillWorking]: AssignmentEventContextPayload;
  [XApiEventKeys.classCodeModalFailure]: ClassCodeEventContextPayload;
  [XApiEventKeys.classCodeModalSuccess]: ClassCodeEventContextPayload;
};
